import { html, css, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

interface Notification {
  id: string;
  message: string;
  type?: 'info' | 'success' | 'warning' | 'error';
  timestamp: number;
  duration?: number; // 通知显示时长（毫秒）
}

/**
 * 通知栏组件
 */
@customElement('notification-bar')
export class NotificationBar extends LitElement {
  static styles = css`
    :host {
      display: block;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 1000;
      pointer-events: none;
    }

    .container {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .notification {
      width: 90%;
      max-width: 600px;
      margin-top: 10px;
      padding: 12px 24px;
      background: rgba(0, 0, 0, 0.9);
      color: #00ff00;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 14px;
      border: 1px solid #00ff00;
      box-shadow: 0 0 10px rgba(0, 255, 0, 0.3);
      text-shadow: 0 0 5px rgba(0, 255, 0, 0.5);
      position: relative;
      overflow: hidden;
      pointer-events: auto;
    }

    .notification::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, 
        transparent 0%, 
        #00ff00 20%, 
        #00ff00 80%, 
        transparent 100%
      );
      animation: scanline 2s linear infinite;
    }

    @keyframes scanline {
      0% {
        transform: translateX(-100%);
      }
      100% {
        transform: translateX(100%);
      }
    }

    .notification.info {
      background: rgba(0, 30, 60, 0.95);
      border-color: #0088ff;
      color: #0088ff;
      text-shadow: 0 0 5px rgba(0, 136, 255, 0.5);
    }

    .notification.success {
      background: rgba(0, 40, 0, 0.95);
      border-color: #00ff00;
      color: #00ff00;
      text-shadow: 0 0 5px rgba(0, 255, 0, 0.5);
    }

    .notification.warning {
      background: rgba(60, 30, 0, 0.95);
      border-color: #ff8800;
      color: #ff8800;
      text-shadow: 0 0 5px rgba(255, 136, 0, 0.5);
    }

    .notification.error {
      background: rgba(40, 0, 0, 0.95);
      border-color: #ff0000;
      color: #ff0000;
      text-shadow: 0 0 5px rgba(255, 0, 0, 0.5);
    }

    .close-button {
      background: none;
      border: none;
      color: inherit;
      cursor: pointer;
      padding: 0 8px;
      font-size: 18px;
      opacity: 0.8;
      text-shadow: inherit;
    }

    .close-button:hover {
      opacity: 1;
    }

    .message {
      flex: 1;
      margin-right: 16px;
      letter-spacing: 0.5px;
    }

    .timestamp {
      font-size: 12px;
      opacity: 0.8;
      margin-left: 12px;
    }
  `;

  @property({ type: Array })
  notifications: Notification[] = [];

  @state()
  private timeouts: Map<string, number> = new Map();

  willUpdate(changedProperties: Map<string, any>) {
    if (changedProperties.has('notifications')) {
      const oldNotifications = changedProperties.get('notifications') || [];
      const newNotifications = this.notifications.filter(
        n => !oldNotifications.find((old: Notification) => old.id === n.id)
      );
      
      // 为新通知设置自动移除
      newNotifications.forEach(notification => {
        if (notification.duration) {
          console.log(`Setting timeout for notification ${notification.id} with duration ${notification.duration}`);
          const timeoutId = window.setTimeout(() => {
            console.log(`Removing notification ${notification.id}`);
            this.removeNotification(notification.id);
          }, notification.duration);
          this.timeouts.set(notification.id, timeoutId);
        }
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // 清除所有计时器
    this.timeouts.forEach(id => window.clearTimeout(id));
    this.timeouts.clear();
  }

  private removeNotification(id: string) {
    console.log(`Removing notification ${id}`);
    // 清除定时器
    if (this.timeouts.has(id)) {
      window.clearTimeout(this.timeouts.get(id));
      this.timeouts.delete(id);
    }
    
    // 移除通知
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.requestUpdate();
  }

  private formatTimestamp(timestamp: number): string {
    const date = new Date(timestamp);
    return date.toLocaleString('zh-CN', {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  render() {
    return html`
      <div class="container">
        ${this.notifications.map(notification => html`
          <div class="notification ${notification.type || ''}">
            <span class="message">${notification.message}</span>
            <span class="timestamp">${this.formatTimestamp(notification.timestamp)}</span>
            <button
              class="close-button"
              @click=${() => this.removeNotification(notification.id)}
            >×</button>
          </div>
        `)}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'notification-bar': NotificationBar;
  }
} 